import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { tomorrowISO } from "@/lib/leads";
import { LeadPipelineFields } from "./LeadFields";

const BLANK = { name: "", phone: "", email: "", travel_date: "", adults: 2, children: 0, message: "", status: "new", follow_up_date: "", follow_up_time: "", source: "whatsapp", trip_type: "", destination: "", budget: "", notes: "" };

const Text = ({ id, label, ...p }) => (
  <div className="space-y-1.5"><Label htmlFor={id}>{label}</Label><Input id={id} className="h-10" data-testid={`${id}-input`} {...p} /></div>
);

export const AddLeadDialog = ({ open, onOpenChange, onSaved }) => {
  const [f, setF] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(BLANK); }, [open]);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v, ...(k === "status" && v === "follow_up" && !s.follow_up_date ? { follow_up_date: tomorrowISO() } : {}) }));
  const inp = (k) => (e) => set(k)(e.target.value);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/admin/enquiries", { ...f, adults: Number(f.adults), children: Number(f.children) || 0, email: f.email || null });
      onSaved(data);
      toast.success("Lead added");
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="add-lead-dialog">
        <DialogHeader><DialogTitle className="text-xl text-navy">Add a lead</DialogTitle></DialogHeader>
        <p className="-mt-2 text-sm text-slate-500">For enquiries received on WhatsApp, Instagram, Facebook, Google or by phone.</p>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Text id="add-lead-name" label="Name *" required minLength={2} value={f.name} onChange={inp("name")} />
            <Text id="add-lead-phone" label="Mobile / WhatsApp *" required type="tel" pattern="^\+?[0-9][0-9\s\-]{6,19}$" value={f.phone} onChange={inp("phone")} />
            <Text id="add-lead-date" label="Travel date" type="date" value={f.travel_date} onChange={inp("travel_date")} />
            <div className="grid grid-cols-2 gap-3">
              <Text id="add-lead-adults" label="Adults *" required type="number" min={1} max={60} value={f.adults} onChange={inp("adults")} />
              <Text id="add-lead-children" label="Children" type="number" min={0} max={40} value={f.children} onChange={inp("children")} />
            </div>
            <Text id="add-lead-message" label="Customer message" value={f.message} onChange={inp("message")} maxLength={1000} />
            <Text id="add-lead-email" label="Email" type="email" value={f.email} onChange={inp("email")} />
          </div>
          <LeadPipelineFields f={f} set={set} prefix="add-lead" />
          <Button type="submit" disabled={saving} className="w-full bg-ocean hover:bg-navy" data-testid="add-lead-submit-button">Save lead</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
